import React, { type ComponentProps } from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  Illustration,
  Typography,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../utils';

import invertedMessages from './messages';

import './ExternalBanner.css';

const AGENTIC_AI_SECURITY_URL =
  'https://saviynt.com/features/agentic-ai-security';
/** Inferred from Illustration; avoids importing IllustrationKind (not always re-exported by DS). */

type ExternalBannerIllustrationKind = NonNullable<
  ComponentProps<typeof Illustration>['kind']
>;

interface ExternalBannerProps {
  heading: string;
  subHeading: string;
  showButton: boolean;
  showClose: boolean;
  buttonText?: string;
  closeExtBanner?: () => void;
  illustration: ExternalBannerIllustrationKind;
}

/**
 * @description Promotional banner with heading, subheading, illustration, and optional CTA button.
 * Supports dismissal via a close button and links to an external resource.
 *
 * @param {ExternalBannerProps} props
 * @param {string} props.heading - Banner headline text.
 * @param {string} props.subHeading - Supporting description text.
 * @param {boolean} props.showButton - Whether to render the CTA button.
 * @param {boolean} props.showClose - Whether to render the dismiss button.
 * @param {string} [props.buttonText] - Label for the CTA button.
 * @param {() => void} [props.closeExtBanner] - Callback invoked when the banner is dismissed.
 * @param {ExternalBannerIllustrationKind} props.illustration - Illustration variant to display.
 * @returns {React.ReactElement} The rendered banner element.
 */
function ExternalBanner({
  heading,
  subHeading,
  buttonText,
  showButton,
  showClose,
  closeExtBanner,
  illustration,
}: ExternalBannerProps) {
  const l = useInvertedLocalize(invertedMessages);

  return (
    <Box className='Ext-ExternalBanner'>
      <Box>
        <Typography
          kind='h2'
          color='--color-background-neutral-subtlest'
          className='Ext-ExternalBanner-heading'>
          {heading}
        </Typography>
        <Typography kind='body1' color='--color-background-neutral-subtlest'>
          {subHeading}
        </Typography>
        {showButton && (
          <div className='Ext-ExternalBanner-btnGroup'>
            <Button
              type='button'
              kind='filled'
              onClick={() => {
                window.open(
                  AGENTIC_AI_SECURITY_URL,
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
              rightIcon={
                <Icon
                  kind='ArrowRight'
                  size='medium'
                  color='neutral-100'
                  data-testid='arrow-right-icon'
                />
              }>
              {buttonText}
            </Button>
            {/* Temporarily commenting out the code until the actual links are available. */}
            {/* <Typography
              kind='body2'
              color='--color-background-neutral-subtlest'>
              <a
                href='https://docs.saviyntcloud.com/'
                target='_blank'
                className='Ext-ExternalBanner-btnGroup-link'
                rel='noopener noreferrer'>
                Learn more about the value of user classification
              </a>
            </Typography> */}
          </div>
        )}
      </Box>
      <Box className='Ext-ExternalBanner-actions'>
        <Illustration kind={illustration} dataTheme='dark' />
        {showClose && closeExtBanner && (
          <div>
            <ButtonIcon
              className='Ext-ExternalBanner-dismiss'
              size='large'
              kind='ghost'
              icon={<Icon kind='Close' />}
              onClick={() => {
                closeExtBanner();
              }}
              ariaLabel={l('Close Banner')}
              dataTheme='dark'
            />
          </div>
        )}
      </Box>
    </Box>
  );
}

export default ExternalBanner;

ExternalBanner.defaultProps = {
  closeExtBanner: () => {},
  buttonText: 'Begin',
};
